import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import TunnelTooltip from './TunnelTooltip';

type TunnelConfig = {
  bgColor: number;
  stoneCount: number;
  tunnelRadius: number;
  tunnelLength: number;
  minDistance: number;
  rotSpeed: number;
  moveSpeed: number;
  stonePlaneSize: number;
};

type TunnelReaction = {
  id: string;
  user: string;
  text: string;
  imageUrl?: string;
  avatarUrl?: string;
};

const DEFAULT_CONFIG: TunnelConfig = {
  bgColor: 0xdfded5,
  stoneCount: 60,
  tunnelRadius: 10,
  tunnelLength: 80,
  minDistance: 4,
  rotSpeed: 0.003,
  moveSpeed: 0.08,
  stonePlaneSize: 6,
};

function createRoundAlphaMap(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    const fallback = document.createElement('canvas');
    fallback.width = 2;
    fallback.height = 2;
    const t = new THREE.CanvasTexture(fallback);
    t.needsUpdate = true;
    return t;
  }

  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, 512, 512);

  const gradient = ctx.createRadialGradient(256, 256, 180, 256, 256, 250);
  gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(256, 256, 250, 0, Math.PI * 2);
  ctx.fill();

  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}

function createStoneTexture(base: string, accent: string): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    const fallback = document.createElement('canvas');
    fallback.width = 2;
    fallback.height = 2;
    const t = new THREE.CanvasTexture(fallback);
    t.needsUpdate = true;
    return t;
  }

  ctx.fillStyle = base;
  ctx.fillRect(0, 0, 256, 256);

  for (let i = 0; i < 80; i++) {
    const x = Math.random() * 256;
    const y = Math.random() * 256;
    const r = 6 + Math.random() * 26;
    ctx.globalAlpha = 0.06 + Math.random() * 0.12;
    ctx.fillStyle = accent;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  const grad = ctx.createRadialGradient(128, 128, 20, 128, 128, 140);
  grad.addColorStop(0, 'rgba(255,255,255,0.10)');
  grad.addColorStop(1, 'rgba(0,0,0,0.18)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 256, 256);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

export default function TunnelBackground({
  config = DEFAULT_CONFIG,
  reactions,
  stoneTextureUrls,
  onStoneClick,
  uiOverlayRefs,
}: {
  config?: TunnelConfig;
  reactions?: TunnelReaction[];
  stoneTextureUrls?: string[];
  onStoneClick?: (reactionId: string) => void;
  uiOverlayRefs?: Array<React.RefObject<HTMLElement | null>>;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [tooltipData, setTooltipData] = useState<{
    user: string;
    text: string;
    avatarUrl?: string;
    imageUrl?: string;
    x: number;
    y: number;
  } | null>(null);
  const setTooltipRef = useRef(setTooltipData);
  setTooltipRef.current = setTooltipData;
  const uiOverlayRefsRef = useRef(uiOverlayRefs);
  uiOverlayRefsRef.current = uiOverlayRefs;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;
    let cleanup: (() => void) | null = null;

    const defaultTextures = [
      createStoneTexture('#c9c2b3', '#7c756a'),
      createStoneTexture('#b7b0a0', '#6e665b'),
      createStoneTexture('#d4cdbc', '#8a8276'),
      createStoneTexture('#c3bbaa', '#6a6358'),
      createStoneTexture('#e0d9c8', '#8e867a'),
    ];

    const pool = (reactions ?? []).filter(
      (r): r is TunnelReaction & { imageUrl: string } => Boolean(r.imageUrl)
    );
    const fromReactions = pool
      .map(r => r.imageUrl)
      .filter((u): u is string => Boolean(u));
    const imageUrlsForTextures = Array.from(new Set(fromReactions));

    const baseTextureUrls =
      imageUrlsForTextures.length > 0
        ? imageUrlsForTextures
        : (stoneTextureUrls ?? []);

    const loadTextures = async (): Promise<THREE.Texture[]> => {
      if (baseTextureUrls.length === 0) return defaultTextures;
      const loadTex = (url: string): Promise<THREE.Texture> =>
        new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            const tex = new THREE.Texture(img);
            tex.needsUpdate = true;
            tex.colorSpace = THREE.SRGBColorSpace;
            resolve(tex);
          };
          img.onerror = () => reject(new Error(`Load failed: ${url}`));
          img.src = url;
        });
      const results = await Promise.allSettled(baseTextureUrls.map(loadTex));
      const loaded = results
        .filter(
          (r): r is PromiseFulfilledResult<THREE.Texture> =>
            r.status === 'fulfilled'
        )
        .map(r => r.value);
      return loaded.length > 0 ? loaded : defaultTextures;
    };

    const init = async () => {
      const stoneTextures = await loadTextures();
      if (cancelled) return;

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(config.bgColor);
      scene.fog = new THREE.Fog(config.bgColor, 20, 60);

      const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
      camera.position.set(0, 0, 0);

      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: false,
      });
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      container.appendChild(renderer.domElement);

      const alphaMapTex = createRoundAlphaMap();

      const wheelGroup = new THREE.Group();
      scene.add(wheelGroup);

      const stones: THREE.Mesh[] = [];
      const positions: THREE.Vector3[] = [];

      const isOverlapping = (pos: THREE.Vector3) => {
        for (const p of positions) {
          if (p.distanceTo(pos) < config.minDistance) return true;
        }
        return false;
      };

      const targetCount = config.stoneCount;
      let attempts = 0;
      while (positions.length < targetCount && attempts < 2000) {
        attempts++;
        const theta = Math.random() * Math.PI * 2;
        const z =
          Math.random() * config.tunnelLength - (config.tunnelLength - 10);
        const x = config.tunnelRadius * Math.cos(theta);
        const y = config.tunnelRadius * Math.sin(theta);
        const pos = new THREE.Vector3(x, y, z);
        if (isOverlapping(pos)) continue;
        positions.push(pos);
      }

      const geometry = new THREE.PlaneGeometry(
        config.stonePlaneSize,
        config.stonePlaneSize
      );

      for (let i = 0; i < positions.length; i++) {
        const reaction = pool[i % Math.max(pool.length, 1)];
        const texIdx =
          reaction?.imageUrl && baseTextureUrls.length > 0
            ? baseTextureUrls.indexOf(reaction.imageUrl)
            : -1;
        const texIndex =
          texIdx >= 0 && stoneTextures[texIdx]
            ? texIdx
            : i % Math.max(stoneTextures.length, 1);
        const material = new THREE.MeshBasicMaterial({
          map: stoneTextures[texIndex] ?? defaultTextures[0],
          color: 0xffffff,
          side: THREE.DoubleSide,
          transparent: true,
          alphaMap: alphaMapTex,
          alphaTest: 0.1,
        });

        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.copy(positions[i]);
        mesh.lookAt(0, 0, mesh.position.z);
        mesh.rotateZ(Math.random() * 0.2);
        mesh.rotateY(Math.PI);
        (mesh as any).userData.comment = reaction;
        wheelGroup.add(mesh);
        stones.push(mesh);
      }

      if (pool.length === 0) {
        let fallbackAttempts = 0;
        while (stones.length < config.stoneCount && fallbackAttempts < 2000) {
          fallbackAttempts++;
          const theta = Math.random() * Math.PI * 2;
          const z =
            Math.random() * config.tunnelLength - (config.tunnelLength - 10);
          const x = config.tunnelRadius * Math.cos(theta);
          const y = config.tunnelRadius * Math.sin(theta);
          const pos = new THREE.Vector3(x, y, z);
          if (isOverlapping(pos)) continue;
          positions.push(pos);
          const texIndex = Math.floor(Math.random() * defaultTextures.length);
          const material = new THREE.MeshBasicMaterial({
            map: defaultTextures[texIndex],
            color: 0xffffff,
            side: THREE.DoubleSide,
            transparent: true,
            alphaMap: alphaMapTex,
            alphaTest: 0.1,
          });
          const mesh = new THREE.Mesh(geometry, material);
          mesh.position.copy(pos);
          mesh.lookAt(0, 0, mesh.position.z);
          mesh.rotateZ(Math.random() * 0.2);
          mesh.rotateY(Math.PI);
          wheelGroup.add(mesh);
          stones.push(mesh);
        }
      }

      const raycaster = new THREE.Raycaster();
      const mouse = new THREE.Vector2(-9999, -9999);
      let lastMouseX = 0;
      let lastMouseY = 0;
      let isOverOverlay = false;
      let isTouchActive = false;
      const isTouchPrimary = window.matchMedia('(pointer: coarse)').matches;
      const TAP_MOVE_THRESHOLD_PX = 14;
      let touchStartX = 0;
      let touchStartY = 0;

      const toLocalPoint = (clientX: number, clientY: number) => {
        const rect = container.getBoundingClientRect();
        return {
          rect,
          inside:
            clientX >= rect.left &&
            clientX <= rect.right &&
            clientY >= rect.top &&
            clientY <= rect.bottom,
        };
      };

      const setRayFromClientPoint = (clientX: number, clientY: number) => {
        const { rect, inside } = toLocalPoint(clientX, clientY);
        if (!inside) {
          mouse.set(-9999, -9999);
          return false;
        }
        mouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;
        lastMouseX = clientX - rect.left + 12;
        lastMouseY = clientY - rect.top + 12;
        return true;
      };

      const isPointOverUIOverlay = (clientX: number, clientY: number) => {
        const overlayRefs = uiOverlayRefsRef.current;
        if (!overlayRefs || overlayRefs.length === 0) return false;
        for (const ref of overlayRefs) {
          const el = ref?.current;
          if (!el) continue;
          const r = el.getBoundingClientRect();
          if (
            clientX >= r.left &&
            clientX <= r.right &&
            clientY >= r.top &&
            clientY <= r.bottom
          ) {
            return true;
          }
        }
        return false;
      };

      const onMouseMove = (event: MouseEvent) => {
        if (isTouchActive) {
          return;
        }
        if (isTouchPrimary) {
          return;
        }
        const rect = container.getBoundingClientRect();
        const inside =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;
        const overUI = isPointOverUIOverlay(event.clientX, event.clientY);
        const el = document.elementFromPoint(event.clientX, event.clientY);
        const overCanvas = el && container.contains(el);
        isOverOverlay = overUI || !inside || !overCanvas;

        if (isOverOverlay) {
          mouse.set(-9999, -9999);
          return;
        }

        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        lastMouseX = event.clientX - rect.left + 12;
        lastMouseY = event.clientY - rect.top + 12;
      };

      window.addEventListener('mousemove', onMouseMove);

      const onTouchStart = (event: TouchEvent) => {
        const touch = event.touches[0];
        if (!touch) return;
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
        isTouchActive = true;
        const hasPoint = setRayFromClientPoint(touch.clientX, touch.clientY);
        if (!hasPoint) {
          isTouchActive = false;
        }
      };

      const onTouchMove = (event: TouchEvent) => {
        if (!isTouchActive) return;
        const touch = event.touches[0];
        if (!touch) return;
        setRayFromClientPoint(touch.clientX, touch.clientY);
      };

      const onTouchEnd = (event: TouchEvent) => {
        const changed = event.changedTouches[0];
        if (!changed) {
          isTouchActive = false;
          mouse.set(-9999, -9999);
          setTooltipRef.current?.(null);
          return;
        }

        const dx = changed.clientX - touchStartX;
        const dy = changed.clientY - touchStartY;
        const isTap =
          dx * dx + dy * dy <= TAP_MOVE_THRESHOLD_PX * TAP_MOVE_THRESHOLD_PX;

        const hasPoint = setRayFromClientPoint(
          changed.clientX,
          changed.clientY
        );
        if (hasPoint && onStoneClick && isTap) {
          raycaster.setFromCamera(mouse, camera);
          const intersects = raycaster.intersectObjects(stones);
          if (intersects.length > 0) {
            const mesh = intersects[0].object as THREE.Mesh & {
              userData: { comment?: TunnelReaction };
            };
            const comment = mesh.userData.comment;
            if (comment?.id) {
              onStoneClick(comment.id);
            }
          }
        }

        isTouchActive = false;
        mouse.set(-9999, -9999);
        setTooltipRef.current?.(null);
      };

      renderer.domElement.addEventListener('touchstart', onTouchStart, {
        passive: true,
      });
      renderer.domElement.addEventListener('touchmove', onTouchMove, {
        passive: true,
      });
      renderer.domElement.addEventListener('touchend', onTouchEnd, {
        passive: true,
      });
      renderer.domElement.addEventListener('touchcancel', onTouchEnd, {
        passive: true,
      });

      const onMouseClick = (event: MouseEvent) => {
        if (isTouchPrimary) {
          return;
        }
        if (!onStoneClick) return;
        const rect = container.getBoundingClientRect();
        const inside =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;
        if (!inside) return;

        const clickX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        const clickY = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        const clickMouse = new THREE.Vector2(clickX, clickY);

        raycaster.setFromCamera(clickMouse, camera);
        const intersects = raycaster.intersectObjects(stones);
        if (intersects.length === 0) return;

        const mesh = intersects[0].object as THREE.Mesh & {
          userData: { comment?: TunnelReaction };
        };
        const comment = mesh.userData.comment;
        if (comment?.id) {
          onStoneClick(comment.id);
        }
      };

      renderer.domElement.addEventListener('click', onMouseClick);

      let raf = 0;
      const resize = () => {
        const w = container.clientWidth || window.innerWidth;
        const h = container.clientHeight || window.innerHeight;
        renderer.setSize(w, h);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
      };

      resize();
      window.addEventListener('resize', resize);

      let isHovering = false;

      const animate = () => {
        raf = window.requestAnimationFrame(animate);

        if (isTouchPrimary && !isTouchActive) {
          setTooltipRef.current?.(null);
          wheelGroup.rotation.z += config.rotSpeed;
          for (const stone of stones) {
            stone.position.z += config.moveSpeed;
            if (stone.position.z > 5) {
              stone.position.z -= config.tunnelLength;
            }
          }
          renderer.render(scene, camera);
          return;
        }

        raycaster.setFromCamera(mouse, camera);
        const intersects = raycaster.intersectObjects(stones);
        isHovering =
          !isOverOverlay &&
          intersects.length > 0 &&
          (!isTouchPrimary || isTouchActive);

        if (!isHovering) {
          wheelGroup.rotation.z += config.rotSpeed;
          for (const stone of stones) {
            stone.position.z += config.moveSpeed;
            if (stone.position.z > 5) {
              stone.position.z -= config.tunnelLength;
            }
          }
        }

        if (!isOverOverlay && intersects.length > 0) {
          const mesh = intersects[0].object as THREE.Mesh & {
            userData: {
              stoneId?: number;
              comment?: TunnelReaction;
            };
          };
          const data = mesh.userData.comment;
          if (data) {
            setTooltipRef.current?.({
              user: data.user,
              text: data.text,
              avatarUrl: data.avatarUrl,
              imageUrl: data.imageUrl,
              x: lastMouseX,
              y: lastMouseY,
            });
          } else {
            setTooltipRef.current?.(null);
          }
        } else {
          setTooltipRef.current?.(null);
        }

        renderer.render(scene, camera);
      };
      animate();

      cleanup = () => {
        window.cancelAnimationFrame(raf);
        window.removeEventListener('resize', resize);
        window.removeEventListener('mousemove', onMouseMove);
        renderer.domElement.removeEventListener('click', onMouseClick);
        renderer.domElement.removeEventListener('touchstart', onTouchStart);
        renderer.domElement.removeEventListener('touchmove', onTouchMove);
        renderer.domElement.removeEventListener('touchend', onTouchEnd);
        renderer.domElement.removeEventListener('touchcancel', onTouchEnd);
        wheelGroup.removeFromParent();
        geometry.dispose();
        alphaMapTex.dispose();
        stoneTextures.forEach(t => t.dispose());
        stones.forEach(s => {
          const mat = s.material as THREE.Material;
          mat.dispose();
        });
        renderer.dispose();
        if (renderer.domElement.parentElement === container) {
          container.removeChild(renderer.domElement);
        }
      };
    };

    init();

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [config, reactions, stoneTextureUrls, onStoneClick]);

  return (
    <div
      ref={containerRef}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
    >
      {tooltipData && (
        <TunnelTooltip
          user={tooltipData.user}
          text={tooltipData.text}
          avatarUrl={tooltipData.avatarUrl}
          imageUrl={tooltipData.imageUrl}
          x={tooltipData.x}
          y={tooltipData.y}
          containerRef={containerRef}
        />
      )}
    </div>
  );
}
